/**
 * @format
 * @module lib/recommended/tgam
 */
import { getUserSophiId, getUserHashId } from "lib/identity/api";
import tgamConfig from "config/tgam";
import { qs, qsa, getProp, hasProp } from "utils/utils";
import ee from "lib/eventemitter";
import api from "lib/recommended/api";
import render from "lib/recommended/render";
import { pageYOffset } from "utils/window-events";
import { mark, measure, getEntriesByName } from "lib/performance/user-timing";
import marketingContainersReady from "lib/marketing/marketing-containers-ready";
import logger from "utils/logger";
import { pixel } from "./impression-pixels";

import initRecommenderContextTest, { sendAnalytics } from "./context-test";

const debugTag = "[RECOMMENDER]";

let featureObjects = []; // of lazy-rendered article lists

/**
 * @method logPerformance
 */
function logPerformance() {
  measure("recommender");
  const entries = getEntriesByName("recommender");
  logger.info(`${debugTag} performance entries:`, entries);
  if (!entries.length) {
    return;
  }
  const duration = parseInt(entries[0].duration, 10);
  logger.info(`${debugTag} timing: ~ ${duration}ms`);
  // for 10% of real-world traffic, report the API response time to GTM
  const sample = parseInt(100 * Math.random(), 10);
  if (sample <= 10 || /recommender=true/.test(window.location.search)) {
    window.analyticsLayer.push({
      event: "simpleEvent",
      simpleEvent: {
        category: "recommender",
        action: "performance",
        label: "API response time",
        value: duration
      }
    });
  }
}

/**
 * @method enableLazyRender
 */
function enableLazyRender() {
  ee.on("windowScroll.throttled", lazyRenderFeatures);
  ee.on("windowResize.throttled", lazyRenderFeatures);
  lazyRenderFeatures();
}

/**
 * @method disableLazyRender
 */
function disableLazyRender() {
  ee.off("windowScroll.throttled", lazyRenderFeatures);
  ee.off("windowResize.throttled", lazyRenderFeatures);
}

/**
 * @method allFeaturesHaveRendered
 * @returns {Boolean}
 */
function allFeaturesHaveRendered() {
  const loaded = featureObjects.filter(feature => {
    return feature.isRendered === true;
  });
  return loaded.length === featureObjects.length;
}

/**
 * @method immediateRender
 */
function immediateRender() {
  featureObjects
    .filter(feature => {
      return (
        (feature.lazyRender === undefined || feature.lazyRender === false) && feature.apiResponse
      );
    })
    .forEach(feature => {
      // 1. A regular recommended articles sub-request response ...
      if (!hasProp(feature, "apiResponse.container_recommendations")) {
        renderFeature(feature);
        return;
      }

      // 2. A marketing container sub-request response ...
      // Marketing containers that exist on production (and that are sent by recommender) may NOT
      // exist on local and dev versions of /zzz_marketing-containers_zzz/ page. In this case, for
      // testing purposes, you would need to ask /zzz_marketing-containers_zzz/ for a specific
      // container by adding param `debugContainerName=name_of_mc_container`. Make sure it actually
      // exists on the page above in the environment you are testing.
      const params = new URLSearchParams(document.location.search);
      const firstItem = "apiResponse.container_recommendations[0].";

      const containerName =
        params.get("debugContainerName") || getProp(feature, firstItem + "name", ""); // feature name

      // Since these two values are only used for analytics, they don't require debug params
      const containerId = getProp(feature, firstItem + "containerId", ""); // feature fingerprint id
      const algorithm = getProp(feature, "apiResponse.algorithm", ""); // used for container selection
      const impressionPixel = pixel(containerName);

      if (containerName.length) {
        marketingContainersReady.resolve({
          containerName,
          containerId,
          algorithm,
          impressionPixel
        });
      }
    });
}

/**
 * @method lazyRenderFeatures
 */
function lazyRenderFeatures() {
  if (allFeaturesHaveRendered()) {
    disableLazyRender();
    return;
  }

  let scrollTop = pageYOffset();
  let viewportHeight = document.documentElement.clientHeight;
  let scrollBottom = scrollTop + viewportHeight;
  let depth = 0.7;

  featureObjects
    .filter(feature => {
      let element = qs(`div[data-id='${feature.dataId}']`);
      return (
        element &&
        feature.lazyRender === true &&
        feature.apiResponse &&
        !feature.isRendered &&
        scrollBottom > element.getBoundingClientRect().top + scrollTop - viewportHeight * depth
      );
    })
    .forEach(feature => {
      renderFeature(feature);
    });
}

/**
 * @method renderFeature
 * @description renders a list of recommended articles
 * @param {Object} feature - an object stored in the `featureObjects` array
 */
function renderFeature(feature) {
  const htmlOutput = render({
    source: "tgam",
    requestId: getProp(feature, "apiResponse.request_id"),
    domId: feature.domId,
    items: getProp(feature, "apiResponse.recommendations"),
    heading: feature.heading,
    style: feature.style
  });
  qs(`div[data-id='${feature.dataId}']`).innerHTML = htmlOutput;
  feature.isRendered = true;
}

/**
 * @method requestParams
 * @description sets up common recommender api request parameters
 * @param {Array} [subRequests=[]] - an array of 1 or more sub requests
 * @returns {Object}
 */
function requestParams(subRequests = []) {
  const visitorId = getUserSophiId() || "";
  const hashId = getUserHashId() || "";
  return {
    sub_requests: subRequests,
    platform: tgamConfig.env.outputType === "mobile" ? "mobile" : "desktop",
    visitor_id: visitorId,
    hash_id: hashId
  };
}

/**
 * Gets the path of a given uri, excluding `/article|video|gallery-slug`.
 * @param {String} path
 * @returns {String} the current path
 */
function getSectionPathName(path = window.location.pathname) {
  let ret = null;
  if (path === "/") {
    return "homepage";
  }
  ["article", "video", "gallery"].forEach(type => {
    if (path.includes(`/${type}`)) {
      ret = path.split(type)[0];
    }
  });
  return ret ? ret : path;
}

/**
 * Determine if the current section/article is sponsored
 * @returns {Boolean}
 */
// function getSponsoredStatus() {
//   return (
//     getProp(tgamConfig, "meta.advContent") === true ||
//     getProp(tgamConfig, "meta.seoKeywords", "").includes("adveditorial")
//   );
// }

/**
 * @method makeBatchRequest
 * @description makes a batch request to the recommender api (one request for all features on the
 * page). The data that gets passed to the API for each feature is contained in the data-request
 * attribute set server side in the feature.jsp file. Upon a successful response, the
 * `featureobject` associated with each feature gets an `apiResponse` added to it. That data is used
 * in the template.js file so that we can pass things like the API request id to omniture.
 * @param {Array} features - of article lists to request stories for each.
 * @param {Object} [testConfig] - a tgam-test experiment
 * @returns {Promise}
 */
function makeBatchRequest(features, testConfig) {
  const isStoryPage = getProp(tgamConfig, "meta.isStoryPage", false);

  const adSequencingNextStoryEnabled =
    getProp(tgamConfig, "adSequencing.nextStoryDisplayed") === true;

  // if the user is not about to churn do not add an additional sub-request for the marketing container
  const requests = features.filter(filterSubRequests);

  const subRequests = requests.map((feature, i) => {
    let params = JSON.parse(feature.dataset.request);
    params.rank = i + 1;
    /* eslint-disable camelcase */
    params.last_content_ids = tgamConfig.meta.contentId ? tgamConfig.meta.contentId : "";
    /* eslint-enable camelcase */
    // if ad sequencing responds with "content_ads: true" and this is "More from this section" on
    // the article page, change the request context.
    if (adSequencingNextStoryEnabled && isStoryPage && /art_same_section/im.test(params.context)) {
      params.context = "ads_recommendations";
      params.campaign_id = getProp(tgamConfig, "adSequencing.campaign");
    }
    if (testConfig && testConfig.variant.toLowerCase() !== "control") {
      const { recoTestMFTScontext, recoTestMFTGAMcontext } = tgamConfig.sophiProps;
      if (params.widget_id === "recommended-art_same_section_mostpopular" && recoTestMFTScontext) {
        params.context = recoTestMFTScontext;
      }
      if (params.widget_id === "recommended-mobile_art_morestories" && recoTestMFTGAMcontext) {
        params.context = recoTestMFTGAMcontext;
      }
    }
    params.newsletter_ids = getProp(tgamConfig, "datalayer.identity.newsletter", "");
    params.section = getSectionPathName();
    params.seo_keywords = getProp(tgamConfig, "meta.seoKeywords", "");
    params.visitor_type = getProp(
      tgamConfig,
      "datalayer.identity.role.name",
      "anonymous"
    ).toLowerCase();
    return params;
  });

  const params = requestParams(subRequests);
  mark("recommender_started");
  return api("tgam", "recommendations", params)
    .then(contents => {
      mark("recommender_done");
      logPerformance();
      contents.forEach(subResponse => {
        featureObjects.forEach(fo => {
          if (subResponse.widget_id === fo.dataId) {
            fo.apiResponse = subResponse;
          }
        });
      });
      immediateRender();
      enableLazyRender();
      testConfig && sendAnalytics(testConfig.name, testConfig.variant);
    })
    .catch(e => {
      features.forEach(feature => {
        feature.style.display = "none";
      });
      logger.error(`${debugTag}`, e);
    });
}

/**
 * @method filterSubRequests
 * @description filter out sub requests to recommender
 * @param {Object} feature - recommender featureObject
 * @returns {Object} - featureObject that passed any of these conditions:
 * 1) if it's an article page and the recommender context is the context defined in `env.recommenderArticleContext`
 * 2) if it's a section page and the context is the context defined in `env.recommenderSectionContext`
 * 3) featureObjects with all other contexts should be returned as it was before
 * Possible context for marketing containers:
 * - art_retainer_container_recommendations
 * - user_container_recommendations (personalized)
 */
function filterSubRequests(feature) {
  const recommenderArticleContext = getProp(tgamConfig, "env.recommenderArticleContext", "");
  const recommenderSectionContext = getProp(tgamConfig, "env.recommenderSectionContext", "");
  const data = JSON.parse(feature.dataset.request);
  const mcArticlePage = data.context === recommenderArticleContext;
  const mcSectionPage = data.context === recommenderSectionContext;
  const { isStoryPage = "", isSectionPage = "" } = tgamConfig.meta;
  const page = isStoryPage ? "story" : isSectionPage ? "sec" : "";
  if (
    (mcArticlePage && page === "story") ||
    (mcSectionPage && page === "sec") ||
    (!mcArticlePage && !mcSectionPage)
  ) {
    return feature;
  }
}

/**
 * @method setup
 * @description Finds all recommended features on the page and initiates a batch request for recommendations.
 * For each discovered feature, adds a new `featureObject` to memory.
 * The properties of a `featureObject` are passed to the c-article-list template.
 */
export default function setup() {
  const features = qsa(".js-recommended-articles");
  if (!features.length) {
    return;
  }
  features.forEach(feature => {
    const d = feature.dataset;
    featureObjects.push({
      lazyRender: d.lazyRender === "true",
      isRendered: false,
      dataId: d.id,
      heading: d.heading,
      style: d.style
    });
  });
  const test = initRecommenderContextTest();
  makeBatchRequest(features, test);
}
